/**
 * CrossFamilyMessageDrawer — private thread scoped to a single
 * `family_relationships` edge (marriage link).  Members of EITHER
 * family can read and post.
 *
 * Props:
 *   edge_id      — the family_relationships id (comes from
 *                  /related-families items[i].edge_id)
 *   onClose      — callback
 *   fromTreeName — root family name (for the header)
 *   toTreeName   — linked family name
 */
import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { X, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function CrossFamilyMessageDrawer({ edgeId, onClose, fromTreeName, toTreeName }) {
  const [state, setState] = useState({ loading: true, items: [], edge: null });
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    try {
      const r = await http.get(`/family-relationships/${edgeId}/messages`);
      setState({ loading: false, items: r.data.items || [], edge: r.data.edge });
    } catch (e) {
      setState({ loading: false, items: [], edge: null });
      if (e?.response?.status === 401) toast.error("Please sign in to view this thread");
      else if (e?.response?.status === 403) toast.error("You aren't part of either family");
    }
  };
  useEffect(() => { load(); }, [edgeId]); // eslint-disable-line
  useEffect(() => {
    // Autoscroll to bottom whenever new messages arrive.
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [state.items.length]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await http.post(`/family-relationships/${edgeId}/messages`, { body: body.trim() });
      setBody("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="cross-family-drawer"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: "var(--wofy-border)" }}>
          <MessageCircle className="w-5 h-5" style={{ color: "#4338CA" }} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Cross-Family Thread</div>
            <div className="font-display text-lg truncate">
              {fromTreeName} <span className="opacity-50">·</span> {toTreeName}
            </div>
          </div>
          <button onClick={onClose} className="wofy-btn-ghost text-sm" data-testid="cross-family-close"><X className="w-4 h-4" /></button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 240 }}>
          {state.loading ? (
            <div className="text-center py-10 opacity-60"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : state.items.length === 0 ? (
            <div className="text-center py-10 opacity-70 text-sm">
              No messages yet — say hi to the {toTreeName} family!
            </div>
          ) : (
            state.items.map((m) => (
              <div key={m.id} className="flex items-start gap-2" data-testid={`cross-family-msg-${m.id}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "#4338CA1A", color: "#4338CA", fontSize: 11, fontWeight: 700 }}>
                  {(m.author_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs opacity-70">
                    <strong>{m.author_name}</strong> · {(m.created_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                  <div className="text-sm mt-0.5 whitespace-pre-wrap">{m.body}</div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-3 border-t flex gap-2" style={{ borderColor: "var(--wofy-border)" }}>
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            placeholder={`Message the ${toTreeName || "other"} family…`}
            className="flex-1 px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="cross-family-input"
            disabled={sending}
          />
          <button onClick={send} disabled={sending || !body.trim()} className="wofy-btn-primary text-sm" data-testid="cross-family-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
